import { z } from 'zod';

/**
 * Premium currencies: Gold and Diamonds.
 *
 * Unlike Kaki Dollars, these can never be earned in the game. They come from
 * exactly three places: buying a pack with real money, receiving them in a
 * player trade, and a staff grant. Nothing a player does in play (jobs, games,
 * the arcade, selling to a shop) pays out Gold or Diamonds. That keeps them
 * scarce, which is what makes the items sold for them rare.
 *
 * Conversion only runs "downhill": Diamonds turn into Gold, and Gold turns
 * into Kaki Dollars. Kaki Dollars can never be turned into Gold or Diamonds,
 * so the free currency cannot be laundered into the paid one.
 */

export const PREMIUM_CURRENCIES = ['gold', 'diamond'] as const;
export type PremiumCurrency = (typeof PREMIUM_CURRENCIES)[number];
export const premiumCurrencySchema = z.enum(PREMIUM_CURRENCIES);

/** Every currency a conversion or a trade can involve. */
export const CURRENCIES = ['money', 'gold', 'diamond'] as const;
export type Currency = (typeof CURRENCIES)[number];
export const currencySchema = z.enum(CURRENCIES);

export const CURRENCY_LABELS: Readonly<Record<Currency, { singular: string; plural: string }>> = Object.freeze({
  money: { singular: 'Kaki Dollar', plural: 'Kaki Dollars' },
  gold: { singular: 'Gold', plural: 'Gold' },
  diamond: { singular: 'Diamond', plural: 'Diamonds' },
});

export const MAX_PREMIUM_BALANCE = 10_000_000;

/* -------------------------------------------------------------------------- */
/* Conversion                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Beta welcome gift: given once to every full account (new accounts on
 * sign-up, existing ones by migration 0004). Kaki Dollars are not part of it:
 * every account starts with STARTING_MONEY.
 */
export const BETA_WELCOME = Object.freeze({ gold: 100, diamond: 3 });

/** Kaki Dollars paid out for one Gold. One-way: Kaki Dollars never buy Gold back. */
export const MONEY_PER_GOLD = 50;
/** Gold that one Diamond is worth. */
export const GOLD_PER_DIAMOND = 100;
/** Cut taken when converting Diamonds into anything, or Gold up into Diamonds. */
export const CONVERSION_FEE_PERCENT = 10;

/** Conversions that are allowed. Anything else is refused. */
export const CONVERSION_PAIRS: ReadonlyArray<readonly [from: Currency, to: Currency]> = [
  ['diamond', 'gold'],
  ['gold', 'diamond'],
  ['gold', 'money'],
  ['diamond', 'money'],
];

export interface ConversionQuote {
  from: Currency;
  to: Currency;
  /** What the player gives up. */
  spend: number;
  /** What the player receives. */
  receive: number;
  /** What the fee cost, in the currency being received. Zero for Gold to Kaki Dollars. */
  fee: number;
}

export function isConversionAllowed(from: Currency, to: Currency): boolean {
  return CONVERSION_PAIRS.some(([a, b]) => a === from && b === to);
}

function feeOf(amount: number): number {
  return Math.ceil((amount * CONVERSION_FEE_PERCENT) / 100);
}

/** Gold charged for one Diamond when converting up: its worth plus the fee. */
export const GOLD_COST_PER_DIAMOND = GOLD_PER_DIAMOND + feeOf(GOLD_PER_DIAMOND);

/**
 * What converting `spend` units of `from` gives.
 *
 *  - Diamond to Gold or Kaki Dollars: worth at the fixed rate, less the fee (rounded up).
 *  - Gold to Diamond: whole Diamonds only, each costing GOLD_COST_PER_DIAMOND, so
 *    `spend` in the result is what is actually charged (leftover Gold is not taken).
 *  - Gold to Kaki Dollars: the fixed rate, no fee.
 *
 * Returns null if the pair is not allowed or the amount would pay nothing.
 */
export function quoteConversion(from: Currency, to: Currency, spend: number): ConversionQuote | null {
  if (!Number.isSafeInteger(spend) || spend <= 0 || !isConversionAllowed(from, to)) return null;

  if (from === 'gold' && to === 'diamond') {
    const receive = Math.floor(spend / GOLD_COST_PER_DIAMOND);
    if (receive <= 0) return null;
    const charged = receive * GOLD_COST_PER_DIAMOND;
    return { from, to, spend: charged, receive, fee: charged - receive * GOLD_PER_DIAMOND };
  }

  const gross = from === 'diamond' && to === 'gold' ? spend * GOLD_PER_DIAMOND : spend * (from === 'gold' ? MONEY_PER_GOLD : MONEY_PER_GOLD * GOLD_PER_DIAMOND);
  const fee = from === 'gold' ? 0 : feeOf(gross);
  return { from, to, spend, receive: gross - fee, fee };
}

/* -------------------------------------------------------------------------- */
/* Packs bought with real money                                               */
/* -------------------------------------------------------------------------- */

export interface PremiumPack {
  id: string;
  label: string;
  currency: PremiumCurrency;
  amount: number;
  /** Extra units already included in `amount`, shown as "+10% bonus". */
  bonus: number;
  /** Price in US cents. */
  priceCents: number;
}

export const PREMIUM_PACKS: readonly PremiumPack[] = Object.freeze([
  { id: 'gold_100', label: 'Handful of Gold', currency: 'gold', amount: 100, bonus: 0, priceCents: 99 },
  { id: 'gold_550', label: 'Bag of Gold', currency: 'gold', amount: 550, bonus: 50, priceCents: 499 },
  { id: 'gold_1200', label: 'Chest of Gold', currency: 'gold', amount: 1200, bonus: 200, priceCents: 999 },
  { id: 'diamond_5', label: 'Few Diamonds', currency: 'diamond', amount: 5, bonus: 0, priceCents: 499 },
  { id: 'diamond_12', label: 'Pouch of Diamonds', currency: 'diamond', amount: 12, bonus: 2, priceCents: 999 },
  { id: 'diamond_30', label: 'Case of Diamonds', currency: 'diamond', amount: 30, bonus: 6, priceCents: 2299 },
] satisfies PremiumPack[]);

export function premiumPack(id: string): PremiumPack | undefined {
  return PREMIUM_PACKS.find((pack) => pack.id === id);
}

export function formatUsd(cents: number): string {
  return `US$${(cents / 100).toFixed(2)}`;
}

/* -------------------------------------------------------------------------- */
/* HTTP shapes                                                                */
/* -------------------------------------------------------------------------- */

export interface PremiumBalances {
  money: number;
  gold: number;
  diamond: number;
}

export interface PremiumLedgerEntry {
  id: string;
  currency: PremiumCurrency;
  delta: number;
  balanceAfter: number;
  reason: string;
  at: number;
}

export interface PremiumConfig {
  packs: readonly PremiumPack[];
  /** Whether a checkout page is configured; without one, packs can be looked at but not bought. */
  purchasesEnabled: boolean;
  moneyPerGold: number;
  goldPerDiamond: number;
  feePercent: number;
  /** Whether Diamonds and Gold can be exchanged at all (off during the beta). */
  conversionEnabled: boolean;
}
